// src/context/ApiContext.jsx
import React, { createContext, useContext, useMemo } from 'react';
import axios from 'axios';
import { WORKER_TOKEN_KEY, ADMIN_TOKEN_KEY, FLOOR_MANAGER_TOKEN_KEY, SUPERADMIN_TOKEN_KEY } from '../constants/tokenKeys';

const ApiContext = createContext(null);
const BASE_URL = process.env.REACT_APP_BASE_URL;

const TOKEN_MAP = [
  { prefix: '/lkart/superadmin', key: SUPERADMIN_TOKEN_KEY },
  { prefix: '/lkart/supervisor', key: FLOOR_MANAGER_TOKEN_KEY },  
  { prefix: '/lkart/admin', key: ADMIN_TOKEN_KEY },             
  { prefix: '/lkart', key: WORKER_TOKEN_KEY },
];

function resolveToken() {
  const path = window.location.pathname;
  const match = TOKEN_MAP.find(({ prefix }) => path.startsWith(prefix));
  return match ? localStorage.getItem(match.key) : null;
}

export const ApiProvider = ({ children }) => {
  const api = useMemo(() => {
    const instance = axios.create({ baseURL: BASE_URL });
    instance.interceptors.request.use((config) => {
      const token = resolveToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    return instance;
  }, []);

  // ── Shared/common endpoints — used by both worker and admin/superadmin pages ──

  const fetchMonthlyInsentive = async (workerId) => {
    const response = await api.get(`/lenskart/worker-monthly-incentive-payout/${workerId}`);
    return response.data;
  };

  const fetchMonthlyPayout = async (workerId) => {
    const response = await api.get(`/lenskart/worker-monthly-referral-payout/${workerId}`);
    return response.data;
  };

  const value = {
    api, // still exposed for one-off calls that don't warrant a named function yet
    fetchMonthlyInsentive,
    fetchMonthlyPayout,
  };

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

export const useApi = () => {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error('useApi must be used within ApiProvider');
  return ctx;
};